/**
 * PriceContext — shared market-data store.
 *
 * Centralises the three most-polled endpoints so every component reads from
 * the same data instead of making duplicate API calls:
 *
 *   • /api/prices/indices    — refreshed every 2 minutes
 *   • /api/prices/commodities — refreshed every 2 minutes (same cadence)
 *   • /api/kite/status       — refreshed every 60 seconds (needs to be fast
 *                               so the TopBar badge reflects re-login quickly)
 *
 * Components consume via usePrices():
 *   const { indicesRaw, commodsRaw, kiteStatus, lastFetched, refresh } = usePrices();
 *
 * indicesRaw  — raw response from /api/prices/indices (or null while loading)
 * commodsRaw  — raw response from /api/prices/commodities (or null while loading)
 * kiteStatus  — raw response from /api/kite/status (or null while loading)
 * lastFetched — Date of last successful indices fetch (drives the "last updated" label)
 * refresh()   — force an immediate re-fetch of all three (used by the ↻ button)
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getIndices, getCommodities, getKiteStatus } from '../api/client';

const PriceContext = createContext(null);

const PRICE_INTERVAL_MS = 2 * 60 * 1000;  // 2 minutes
const KITE_INTERVAL_MS  = 60 * 1000;       // 1 minute

export function PriceProvider({ children }) {
  const [indicesRaw,   setIndicesRaw]   = useState(null);
  const [commodsRaw,   setCommodsRaw]   = useState(null);
  const [kiteStatus,   setKiteStatus]   = useState(null);
  const [lastFetched,  setLastFetched]  = useState(null);
  const [indicesStale, setIndicesStale] = useState(false);

  // Ref so the interval callback always sees the latest fetchAll
  const fetchingRef = useRef(false);

  const fetchKite = useCallback(() => {
    getKiteStatus()
      .then(s  => setKiteStatus(s))
      .catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    if (fetchingRef.current) return;   // guard against concurrent calls
    fetchingRef.current = true;
    try {
      const [idxResult, commodResult, kiteResult] = await Promise.allSettled([
        getIndices(),
        getCommodities(),
        getKiteStatus(),
      ]);

      if (idxResult.status === 'fulfilled') {
        const v = idxResult.value;
        if (!v._stale && !v._error && v.indices) {
          // Genuine fresh data — update everything honestly
          setIndicesRaw(v);
          setLastFetched(new Date());
          setIndicesStale(false);
        } else if (v._stale && v.indices) {
          // apiFetch returned stale cache — keep showing it but flag as stale;
          // do NOT update lastFetched (preserve the true last-good timestamp)
          setIndicesRaw(v);
          setIndicesStale(true);
        } else {
          // Empty error (no prior cache) — keep existing indicesRaw untouched
          setIndicesStale(true);
        }
      }
      if (commodResult.status === 'fulfilled') setCommodsRaw(commodResult.value);
      if (kiteResult.status   === 'fulfilled') setKiteStatus(kiteResult.value);
    } catch (e) {
      console.warn('[PriceContext] fetchAll error:', e);
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Initial load
    fetchAll();

    // Periodic price refresh
    const priceIv = setInterval(fetchAll, PRICE_INTERVAL_MS);

    // Faster Kite status polling (so badge updates quickly after re-login)
    const kiteIv  = setInterval(fetchKite, KITE_INTERVAL_MS);

    // Re-fetch when user returns to tab (e.g. after completing Kite OAuth)
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchAll();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(priceIv);
      clearInterval(kiteIv);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchAll, fetchKite]);

  return (
    <PriceContext.Provider
      value={{ indicesRaw, commodsRaw, kiteStatus, lastFetched, indicesStale, refresh: fetchAll }}
    >
      {children}
    </PriceContext.Provider>
  );
}

export const usePrices = () => useContext(PriceContext);
